import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiActivity,
  FiArrowRight,
  FiCheckCircle,
  FiClock,
  FiGlobe,
  FiHeart,
  FiMessageCircle,
  FiShield,
  FiUsers
} from 'react-icons/fi';
import { useLanguage } from '../i18n/LanguageContext';
import { useAuth } from './AuthContext';
import { useCareCoordinator } from '../care/CareCoordinatorContext';
import { RecommendationPanel, SubjectSwitcher } from './care/CarePanels';
import './HomePage.css';

const COPY = {
  fr: {
    title: 'Votre parcours de sante, coordonne.',
    subtitle: 'Un concierge intelligent pour votre sante',
    inputPlaceholder: "Comment pouvons-nous vous aider aujourd'hui ?",
    quickSuggestions: [
      { label: 'Soins a domicile', route: 'caregiver', prompt: 'J’ai besoin de soins a domicile pour un proche.' },
      { label: 'Soins des pieds', route: 'other', prompt: 'Je cherche des soins des pieds a domicile.' },
      { label: 'Soins infirmiers', route: 'nurse', prompt: 'J’ai besoin de soins infirmiers a domicile.' },
      { label: 'Sante mentale', route: 'other', prompt: 'Je souhaite parler a un professionnel en sante mentale.' }
    ],
    heroNote: "Decrivez un symptome, un besoin, une langue preferee ou une disponibilite. Le concierge prepare la suite.",
    primaryAction: 'Parler au concierge',
    secondaryAction: 'Creer un compte',
    howItWorks: [
      'Le concierge comprend votre besoin',
      'Les details cliniques et logistiques sont resumes automatiquement',
      'Les meilleurs professionnels vous sont proposes sans friction'
    ],
    aiTitle: "Efficacite par l'IA. Soins centres sur l'humain.",
    aiFeatures: [
      { icon: FiActivity, title: 'IA agentique', body: 'Le concierge pose les bonnes questions, clarifie et orchestre la suite du parcours.' },
      { icon: FiCheckCircle, title: 'Professionnels verifies', body: 'Les correspondances conservent les filtres et la logique de matching existants.' },
      { icon: FiGlobe, title: 'Service multilingue', body: 'Le parcours s’adapte aux preferences linguistiques partagees dans la conversation.' },
      { icon: FiHeart, title: "Centre sur l'humain", body: 'Moins de formulaires, plus de clarte, de contexte et de continuite.' }
    ],
    metricsTitle: 'The KnowMediQ Community Impact',
    metrics: [
      { value: '500+', label: 'familles' },
      { value: '10,000+', label: 'heures de soins' },
      { value: '15+', label: 'langues' },
      { value: '98%', label: 'satisfaction' }
    ],
    testimonialsTitle: 'Histoires de nos patients',
    testimonials: [
      {
        quote: "Le concierge a compris la situation de ma mere en quelques messages et nous a dirigees vers la bonne infirmiere sans que je remplisse un long formulaire.",
        name: 'Sophie M.',
        location: 'Laval, QC',
        service: 'Soins infirmiers a domicile'
      },
      {
        quote: "Nous avons pu expliquer l’urgence, la langue preferee et les disponibilites directement dans la conversation. Le resultat etait clair et rassurant.",
        name: 'Karim A.',
        location: 'Montreal, QC',
        service: 'Coordination multilingue'
      }
    ],
    trustTitle: 'Signaux de confiance',
    trustItems: [
      'Conformite PIPEDA',
      'Paiements securises',
      'Professionnels certifies'
    ],
    signedInTitle: 'Actions suggerees par le concierge',
    providerPortal: 'Portail clinicien'
  },
  en: {
    title: 'Your health journey, coordinated.',
    subtitle: 'An intelligent concierge for your care',
    inputPlaceholder: 'How can we help you today?',
    quickSuggestions: [
      { label: 'Home care', route: 'caregiver', prompt: 'I need home care support for a loved one.' },
      { label: 'Foot care', route: 'other', prompt: 'I am looking for foot care at home.' },
      { label: 'Nursing care', route: 'nurse', prompt: 'I need nursing care at home.' },
      { label: 'Mental health', route: 'other', prompt: 'I want to speak with a mental health professional.' }
    ],
    heroNote: 'Share a symptom, care need, preferred language, or availability. The concierge prepares the next step.',
    primaryAction: 'Talk to the concierge',
    secondaryAction: 'Create account',
    howItWorks: [
      'The concierge understands the need',
      'Clinical and logistics details are summarized automatically',
      'Top providers are surfaced without friction'
    ],
    aiTitle: 'AI efficiency. Human-centered care.',
    aiFeatures: [
      { icon: FiActivity, title: 'Agentic AI', body: 'The concierge asks the right follow-up questions and orchestrates the next step.' },
      { icon: FiCheckCircle, title: 'Verified professionals', body: 'Matching continues to use the existing provider and service logic.' },
      { icon: FiGlobe, title: 'Multilingual service', body: 'The journey adapts to language preferences shared during the conversation.' },
      { icon: FiHeart, title: 'Human-centered', body: 'Less form friction, more clarity, context, and continuity.' }
    ],
    metricsTitle: 'The KnowMediQ Community Impact',
    metrics: [
      { value: '500+', label: 'families' },
      { value: '10,000+', label: 'hours of care' },
      { value: '15+', label: 'languages' },
      { value: '98%', label: 'satisfaction' }
    ],
    testimonialsTitle: 'Patient stories',
    testimonials: [
      {
        quote: 'The concierge understood my mother’s situation in a few messages and routed us to the right nurse without a long intake form.',
        name: 'Sophie M.',
        location: 'Laval, QC',
        service: 'In-home nursing'
      },
      {
        quote: 'We shared the urgency, language preference, and availability in the chat and got a much clearer booking path.',
        name: 'Karim A.',
        location: 'Montreal, QC',
        service: 'Multilingual coordination'
      }
    ],
    trustTitle: 'Trust signals',
    trustItems: [
      'PIPEDA aligned',
      'Secure payments',
      'Certified professionals'
    ],
    signedInTitle: 'Concierge suggestions',
    providerPortal: 'Clinician portal'
  }
};

function HomePage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user, signupPatient, loginProfessional } = useAuth();
  const {
    recommendations,
    managedSubjects,
    activeSubjectId,
    setActiveSubjectId,
    recordRecommendationAction
  } = useCareCoordinator();
  const [prompt, setPrompt] = useState('');

  const copy = COPY[language] || COPY.en;

  const prefillFromPrompt = useMemo(() => {
    const normalized = prompt.toLowerCase();
    if (/infirm|nursing|blood|plaie|pansement|injection/.test(normalized)) return 'nurse';
    if (/domicile|home care|aidant|caregiver|support/.test(normalized)) return 'caregiver';
    return 'other';
  }, [prompt]);

  const handleLaunch = (customPrompt, routeOverride) => {
    const freeText = String(customPrompt || prompt || '').trim();
    navigate('/booking', {
      state: {
        conciergePrefill: {
          freeText,
          selectedRoute: routeOverride || prefillFromPrompt || '',
          source: 'homepage-concierge'
        }
      }
    });
  };

  const handleRecommendationAction = (recommendation, action) => {
    recordRecommendationAction(recommendation, action);
    if (action === 'defer') return;
    navigate('/booking', { state: { conciergePrefill: recommendation.bookingPrefill || null } });
  };

  return (
    <div className="home-page--ai">
      <section className="ai-hero">
        <div className="km-page-shell ai-hero__shell">
          <div className="ai-hero__copy">
            <span className="km-eyebrow">KnowMediQ AI Concierge</span>
            <h1>{copy.title}</h1>
            <p>{copy.subtitle}</p>
          </div>

          <div className="ai-hero__chat km-card">
            <div className="ai-hero__chat-top">
              <div className="ai-hero__assistant">
                <span className="ai-hero__assistant-icon"><FiMessageCircle /></span>
                <div>
                  <strong>KnowMediQ Concierge</strong>
                  <span>{language === 'fr' ? 'En ligne. Repond en quelques secondes.' : 'Online. Replies in seconds.'}</span>
                </div>
              </div>
              <span className="ai-hero__assistant-badge"><FiShield /> PIPEDA</span>
            </div>

            <div className="ai-hero__composer">
              <textarea
                rows={1}
                value={prompt}
                placeholder={copy.inputPlaceholder}
                onChange={(event) => setPrompt(event.target.value)}
              />
              <button className="km-button ai-hero__composer-button" type="button" onClick={() => handleLaunch()}>
                <span>{copy.primaryAction}</span>
                <FiArrowRight />
              </button>
            </div>

            <div className="ai-hero__chips" aria-label={language === 'fr' ? 'Suggestions rapides' : 'Quick suggestions'}>
              {copy.quickSuggestions.map((suggestion) => (
                <button
                  key={suggestion.label}
                  type="button"
                  className="ai-hero__chip"
                  onClick={() => {
                    setPrompt(suggestion.prompt);
                    handleLaunch(suggestion.prompt, suggestion.route);
                  }}
                >
                  {suggestion.label}
                </button>
              ))}
            </div>

            <div className="ai-hero__footer">
              <p>{copy.heroNote}</p>
              <div className="ai-hero__meta">
                {copy.howItWorks.map((item) => (
                  <span key={item}><FiCheckCircle /> {item}</span>
                ))}
              </div>
              {!user ? (
                <div className="ai-hero__actions">
                  <button className="km-button-secondary" type="button" onClick={() => signupPatient()}>
                    {copy.secondaryAction}
                  </button>
                  <button className="ai-hero__text-link" type="button" onClick={() => loginProfessional()}>
                    {copy.providerPortal}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      {user && recommendations.length ? (
        <section className="km-section">
          <div className="km-page-shell ai-dashboard-strip">
            <SubjectSwitcher managedSubjects={managedSubjects} activeSubjectId={activeSubjectId} onChange={setActiveSubjectId} />
            <RecommendationPanel
              title={copy.signedInTitle}
              recommendations={recommendations.slice(0, 3)}
              onAction={handleRecommendationAction}
            />
          </div>
        </section>
      ) : null}

      <section className="km-section ai-value">
        <div className="km-page-shell ai-section-shell">
          <div className="ai-section-heading">
            <span className="km-eyebrow">AI-first care</span>
            <h2>{copy.aiTitle}</h2>
          </div>
          <div className="ai-value__grid">
            {copy.aiFeatures.map(({ icon: Icon, title, body }) => (
              <article key={title} className="ai-value__card km-card">
                <span className="ai-value__icon"><Icon /></span>
                <h3>{title}</h3>
                <p>{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="km-section ai-metrics">
        <div className="km-page-shell ai-section-shell">
          <div className="ai-section-heading ai-section-heading--center">
            <span className="km-eyebrow">Community impact</span>
            <h2>{copy.metricsTitle}</h2>
          </div>
          <div className="ai-metrics__grid">
            {copy.metrics.map((metric) => (
              <article key={metric.label} className="ai-metrics__card">
                <strong>{metric.value}</strong>
                <span>{metric.label}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="km-section ai-testimonials">
        <div className="km-page-shell ai-section-shell">
          <div className="ai-section-heading ai-section-heading--center">
            <span className="km-eyebrow">Patient trust</span>
            <h2>{copy.testimonialsTitle}</h2>
          </div>
          <div className="ai-testimonials__grid">
            {copy.testimonials.map((item) => (
              <article key={`${item.name}-${item.service}`} className="ai-testimonials__card km-card">
                <p className="ai-testimonials__quote">"{item.quote}"</p>
                <div className="ai-testimonials__meta">
                  <strong>{item.name}</strong>
                  <span>{item.location}</span>
                  <small>{item.service}</small>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="km-section ai-trust">
        <div className="km-page-shell ai-section-shell">
          <div className="ai-section-heading ai-section-heading--center">
            <span className="km-eyebrow">Safety first</span>
            <h2>{copy.trustTitle}</h2>
          </div>
          <div className="ai-trust__grid">
            {copy.trustItems.map((item) => (
              <div key={item} className="ai-trust__card">
                <span className="ai-trust__icon"><FiShield /></span>
                <strong>{item}</strong>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
